import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { CalendarDays, FileText, Users } from "lucide-react";

interface SubtaskSummaryChipsProps {
  dueDate: string | null;
  dueTime?: string | null;
  assigneeNames?: string[];
  showAssignee: boolean;
  description?: string | null;
}

/**
 * Chips compactos exibidos no estado colapsado de uma subtarefa
 * (descrição, data/horário e responsáveis, quando definidos).
 */
export function SubtaskSummaryChips({
  dueDate,
  dueTime,
  assigneeNames = [],
  showAssignee,
  description,
}: SubtaskSummaryChipsProps) {
  const visibleAssignees = showAssignee
    ? assigneeNames.filter((name) => Boolean(name?.trim()))
    : [];
  const hasAssignees = visibleAssignees.length > 0;
  const hasDescription = Boolean(description?.trim());
  if (!dueDate && !hasAssignees && !hasDescription) return null;

  const assigneeLabel =
    visibleAssignees.length <= 2
      ? visibleAssignees.join(", ")
      : `${visibleAssignees.slice(0, 2).join(", ")} +${visibleAssignees.length - 2}`;

  return (
    <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
      {hasDescription && (
        <span className="hidden max-w-[140px] items-center gap-1 rounded-full bg-muted px-2 py-0.5 sm:flex">
          <FileText className="h-3 w-3 shrink-0" />
          <span className="truncate">{description}</span>
        </span>
      )}
      {dueDate && (
        <span className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5">
          <CalendarDays className="h-3 w-3" />
          {format(parseISO(dueDate), "dd MMM", { locale: ptBR })}
          {dueTime ? ` · ${dueTime}` : ""}
        </span>
      )}
      {hasAssignees && (
        <span className="flex max-w-[160px] items-center gap-1 rounded-full bg-muted px-2 py-0.5">
          <Users className="h-3 w-3 shrink-0" />
          <span className="truncate">{assigneeLabel}</span>
        </span>
      )}
    </div>
  );
}
